/**
 * <CallControls /> — toolbar inferior da sala de chamada (`<InlineCallRoom>`) com 4 botões:
 * mic, camera, screen-share, e end-call.
 *
 * Usa `useLocalParticipant` do `@livekit/components-react` para enable/disable
 * de tracks. End emite callback `onEnd("normal")` (ou abre confirm dialog
 * antes se a chamada estiver gravando).
 *
 * Refs:
 * - Story 3.2.2 AC11
 * - LiveKit React useLocalParticipant: https://docs.livekit.io/reference/components/react/hook/uselocalparticipant/
 */

import { useCallback, useEffect, useState } from "react";
import { useLocalParticipant, useMaybeRoomContext } from "@livekit/components-react";
import { Track } from "livekit-client";
import {
  Mic,
  MicOff,
  Monitor,
  MonitorUp,
  PhoneOff,
  SwitchCamera,
  Video,
  VideoOff,
} from "lucide-react";

import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export type CallControlsProps = {
  onEnd: (reason: "normal" | "forced") => void;
  isRecording?: boolean;
  /** Flutuar sobre o vídeo (padrão) ou ocupar uma linha própria.
   *
   *  **Por que virou opção (09/08/2026).** Flutuando `absolute bottom-6`, a
   *  barra cobria a galeria de participantes no celular — que fica justamente
   *  na parte de baixo da área de vídeo. O Augusto: *"os botões de desligar, de
   *  câmera e de sair ficam na frente disso… simplesmente não consigo ver a
   *  galeria"*.
   *
   *  Sobreposição já causou três defeitos nesta tela. Numa área alta e larga
   *  (vídeo em tela cheia) flutuar é bonito e não atrapalha; numa tela de
   *  celular, com galeria embaixo, distribuir é a única forma de garantir que
   *  as duas coisas coexistam — sem empilhamento para acertar. */
  flutuante?: boolean;
};

/** O rótulo mostra o que a faixa está REALMENTE usando, não o que pedimos.
 *
 *  `getSettings().facingMode` é o navegador reportando o estado real. Exibir a
 *  nossa intenção faria o rótulo mudar mesmo quando a troca falhasse — que foi
 *  exatamente o tipo de mentira que fez este defeito demorar a aparecer.
 *
 *  Sem `facingMode` (webcam de desktop não reporta), cai para "câmera N".
 */
function rotuloDaCamera(
  sala: ReturnType<typeof useMaybeRoomContext>,
  cameras: MediaDeviceInfo[],
  indice: number
): string {
  const faixa = sala?.localParticipant?.getTrackPublication?.(Track.Source.Camera);
  const modo = faixa?.track?.mediaStreamTrack?.getSettings?.().facingMode;
  if (modo === "user") return "frontal";
  if (modo === "environment") return "traseira";
  return `câmera ${Math.min(indice + 1, Math.max(cameras.length, 1))}`;
}

export function CallControls({ onEnd, isRecording = false, flutuante = true }: CallControlsProps) {
  const { localParticipant } = useLocalParticipant();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Defesa: em ambiente de teste sem `<LiveKitRoom>` ancestral, o hook
  // pode devolver objeto fake. Operações idempotentes garantem ausência de
  // crash em snapshot tests.
  const micEnabled = localParticipant?.isMicrophoneEnabled ?? false;
  const camEnabled = localParticipant?.isCameraEnabled ?? false;
  const screenEnabled = Boolean(localParticipant?.getTrackPublication?.(Track.Source.ScreenShare));

  const toggleMic = async () => {
    await localParticipant?.setMicrophoneEnabled?.(!micEnabled);
  };

  const toggleCam = async () => {
    await localParticipant?.setCameraEnabled?.(!camEnabled);
  };

  /* ------------------------------------------------------------------
   * Inverter câmera (frontal ⇄ traseira).
   *
   * O Augusto: *"para dispositivos móveis, seja Android, seja iPhone, deve
   * permitir inverter a câmera, porque a pessoa está fazendo uma chamada de
   * vídeo e precisa mostrar alguma coisa — tem que usar a câmera de trás."*
   *
   * O botão aparece por CAPACIDADE, não por tipo de aparelho: quem tem duas
   * câmeras ganha o botão, seja celular ou notebook com webcam externa. Testar
   * "é celular?" esconderia o recurso de quem o tem e o mostraria, morto, para
   * quem não tem.
   * ------------------------------------------------------------------ */
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [indiceDaCamera, setIndiceDaCamera] = useState(0);
  const [invertendo, setInvertendo] = useState(false);
  const temMaisDeUmaCamera = cameras.length > 1;
  const sala = useMaybeRoomContext();

  /**
   * Conta as câmeras do aparelho.
   *
   * **O defeito de 08/08/2026.** A versão anterior chamava isto UMA vez, na
   * montagem, com este comentário:
   *
   *     // `enumerateDevices` só revela os RÓTULOS depois da permissão,
   *     // mas a CONTAGEM já vem antes — e contagem é tudo que precisamos.
   *
   * A afirmação é FALSA, e eu a escrevi sem medir. Antes de o
   * `getUserMedia` ser aceito, o navegador devolve **uma entrada genérica por
   * tipo** — não a lista real. No celular, `videoinput` tem tamanho 1 até a
   * permissão sair, então `> 1` dava falso e o botão de inverter NUNCA
   * aparecia.
   *
   * Confirmado no servidor: na chamada do Augusto o celular publicou um único
   * `trackID`, sem nenhuma republicação. A troca jamais aconteceu.
   *
   * Agora recontamos nos três momentos em que a lista pode mudar: montagem,
   * quando a câmera liga (permissão concedida) e no evento `devicechange`
   * (que o próprio navegador dispara ao revelar os dispositivos).
   */
  const contarCameras = useCallback(async () => {
    try {
      const dispositivos = await navigator.mediaDevices?.enumerateDevices?.();
      const video = (dispositivos ?? []).filter((d) => d.kind === "videoinput");
      // Guardamos a LISTA, não só a contagem: a troca acontece por `deviceId`,
      // que é inequívoco. `facingMode` depende de o navegador honrar a
      // restrição, e no celular do Augusto ele não honrou.
      if (video.length) setCameras(video);
    } catch {
      // Sem suporte ou sem permissão: segue com a contagem anterior. Não
      // zeramos, para não fazer o botão piscar e sumir durante a chamada.
    }
  }, []);

  // `camEnabled` na dependência é o ponto todo: quando a câmera liga, a
  // permissão já saiu e só então o navegador revela a lista de verdade.
  useEffect(() => {
    void contarCameras();
  }, [contarCameras, camEnabled]);

  useEffect(() => {
    const midia = navigator.mediaDevices;
    if (!midia?.addEventListener) return;
    const aoMudar = () => void contarCameras();
    midia.addEventListener("devicechange", aoMudar);
    return () => midia.removeEventListener("devicechange", aoMudar);
  }, [contarCameras]);

  const trocarCamera = async () => {
    if (invertendo || cameras.length < 2) return;
    const proximo = (indiceDaCamera + 1) % cameras.length;
    const alvo = cameras[proximo];
    setInvertendo(true);
    try {
      /*
       * `switchActiveDevice` — e NÃO `setCameraEnabled(true, {facingMode})`.
       *
       * **O defeito de 08/08/2026.** A versão anterior chamava
       * `setCameraEnabled(true, { facingMode })` numa câmera JÁ publicada. A
       * própria documentação da biblioteca diz o que isso faz:
       *
       *     // "If a track has already published, it'll mute or unmute the track."
       *     if (track) { yield track.unmute(); }
       *
       * As opções de captura são DESCARTADAS. Sem erro, sem republicação, sem
       * troca — um no-op que resolve com sucesso. O log do LiveKit confirmou:
       * o celular manteve um único `trackID` nas duas tentativas do Augusto.
       *
       * Passar o argumento certo para a função errada é o pior tipo de erro de
       * API: ela ACEITA o valor e o ignora. Meus testes registravam a chamada —
       * e a chamada estava correta; era o efeito que não existia.
       *
       * Duas mudanças aqui:
       *
       * 1. Troca por `deviceId` explícito, ciclando a lista. Não depende de o
       *    navegador honrar `facingMode` — que é justamente o que falhou.
       * 2. `switchActiveDevice` DEVOLVE se a troca surtiu efeito (compara o
       *    deviceId pedido com o das settings reais da faixa). Isso mata a
       *    classe do no-op silencioso: agora dá para saber e avisar.
       */
      const trocou = await sala?.switchActiveDevice("videoinput", alvo.deviceId);
      if (trocou === false) {
        toast.error("O aparelho não aceitou trocar de câmera.");
        return;
      }
      setIndiceDaCamera(proximo);
    } catch (erro) {
      const motivo = erro instanceof Error ? erro.message : "motivo desconhecido";
      toast.error(`Não foi possível trocar de câmera: ${motivo}`);
    } finally {
      setInvertendo(false);
    }
  };

  const toggleScreen = async () => {
    await localParticipant?.setScreenShareEnabled?.(!screenEnabled);
  };

  const handleEndClick = () => {
    if (isRecording) {
      setConfirmOpen(true);
      return;
    }
    onEnd("normal");
  };

  const handleEndConfirm = () => {
    setConfirmOpen(false);
    onEnd("normal");
  };

  return (
    <>
      <div
        data-testid="call-controls"
        className={cn(
          "flex items-center justify-center gap-3 bg-zinc-900/95 shadow-xl",
          flutuante
            ? "absolute bottom-6 left-1/2 z-20 -translate-x-1/2 rounded-full px-6 py-3"
            : // Em linha: ocupa a largura, com folga menor. Rola na horizontal
              // se os botões não couberem numa tela estreita — melhor que
              // transbordar por cima do vídeo.
              "w-full gap-2 overflow-x-auto rounded-none px-2 py-2"
        )}
      >
        <Button
          aria-label={micEnabled ? "Desligar microfone" : "Ligar microfone"}
          variant="ghost"
          size="lg"
          onClick={toggleMic}
          className={`rounded-full size-12 ${micEnabled ? "text-green-400 hover:text-green-300" : "text-red-400 hover:text-red-300"}`}
        >
          {micEnabled ? <Mic className="size-6" /> : <MicOff className="size-6" />}
        </Button>

        <Button
          aria-label={camEnabled ? "Desligar câmera" : "Ligar câmera"}
          variant="ghost"
          size="lg"
          onClick={toggleCam}
          className={`rounded-full size-12 ${camEnabled ? "text-green-400 hover:text-green-300" : "text-zinc-300 hover:text-white"}`}
        >
          {camEnabled ? <Video className="size-6" /> : <VideoOff className="size-6" />}
        </Button>

        {temMaisDeUmaCamera && (
          // Coluna: botão em cima, rótulo EMBAIXO e FORA dele.
          //
          // O rótulo estava dentro, posicionado de forma absoluta. No print do
          // Augusto o botão aparecia como um círculo branco desproporcional,
          // maior que os vizinhos — o texto dentro brigava com o
          // dimensionamento da variante. Fora, cada um tem seu espaço.
          <div className="flex shrink-0 flex-col items-center gap-0.5">
            <Button
              aria-label={`Inverter câmera (usando: ${rotuloDaCamera(sala, cameras, indiceDaCamera)})`}
              title="Inverter câmera (frontal / traseira)"
              variant="ghost"
              size="lg"
              onClick={trocarCamera}
              disabled={invertendo || !camEnabled}
              data-testid="inverter-camera"
              data-camera={String(indiceDaCamera)}
              className="rounded-full size-12 text-zinc-300 hover:text-white"
            >
              <SwitchCamera className="size-6" />
            </Button>
            <span
              className="text-[10px] leading-none text-zinc-400"
              data-testid="camera-atual-rotulo"
            >
              {rotuloDaCamera(sala, cameras, indiceDaCamera)}
            </span>
          </div>
        )}

        <Button
          aria-label={screenEnabled ? "Parar compartilhamento de tela" : "Compartilhar tela"}
          variant="ghost"
          size="lg"
          onClick={toggleScreen}
          className={`rounded-full size-12 ${screenEnabled ? "text-blue-400 hover:text-blue-300" : "text-zinc-300 hover:text-white"}`}
        >
          {screenEnabled ? <MonitorUp className="size-6" /> : <Monitor className="size-6" />}
        </Button>

        <Button
          aria-label="Encerrar chamada"
          variant="destructive"
          size="lg"
          onClick={handleEndClick}
          className="rounded-full size-12"
        >
          <PhoneOff className="size-6" />
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Encerrar chamada gravada?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta chamada está sendo gravada. Encerrar agora finaliza a gravação e sobe o arquivo
              para o storage. Tem certeza?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar na chamada</AlertDialogCancel>
            <AlertDialogAction onClick={handleEndConfirm}>
              Encerrar e finalizar gravação
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
